import { BrowsingSignals } from '@theme/browsing-signals';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Universe config — handles + tags from live catalogue (Tajinebanane).
 * Never source from seconde-main.
 */
const UNIVERSES = {
  kids: {
    primary: 'kids',
    handles: ['kids', 'accessoires-enfant'],
    tags: ['kids', 'enfant'],
    typeHints: ['kids'],
  },
  grossesse: {
    primary: 'vetements-maternite',
    handles: [
      'vetements-maternite',
      'sweatshirts-grossesse',
      'bas-de-grossesse',
      'tee-shirts-grossesse',
      'robe-grossesse',
      'legging-de-grossesse',
      'starter-pack-grossesse',
      'les-indispensables-de-la-maternite',
    ],
    tags: ['grossesse', 'nonallaitant'],
    typeHints: ['maternity', 'grossesse'],
    /** Appoint: breastfeeding products tagged grossesse */
    appointPrimary: 'vetements-allaitement',
    appointRequiresTag: 'grossesse',
  },
  allaitement: {
    primary: 'vetements-allaitement',
    handles: [
      'vetements-allaitement',
      'tee-shirts-allaitement',
      'pulls-sweatshirts-allaitement',
      'robes-allaitement',
      'debardeurs-dallaitement',
      'pyjamas-allaitement',
      'sous-vetements-dallaitement',
      'maillots-de-bain-allaitement',
    ],
    tags: ['allaitant'],
    typeHints: ['breastfeeding', 'allait'],
  },
  accessoires: {
    primary: 'accessoires',
    handles: ['accessoires'],
    tags: ['accessoire', 'accessoires'],
    typeHints: ['accessor'],
  },
};

const SECONDE_MAIN_HANDLE = 'seconde-main';
const SECONDE_MAIN_RE = /seconde[\s_-]?main|occasion|preloved|friperie/i;

/**
 * @param {string} text
 */
function isSecondeMainText(text) {
  return SECONDE_MAIN_RE.test(text || '');
}

/**
 * @param {{ tags?: string[], handle?: string, product_type?: string, type?: string }} product
 */
function isSecondeMainProduct(product) {
  if (isSecondeMainText(product.handle)) return true;
  if (isSecondeMainText(product.product_type || product.type || '')) return true;
  const tags = product.tags || [];
  return tags.some((t) => isSecondeMainText(String(t)));
}

/**
 * @param {string} handle
 * @returns {keyof typeof UNIVERSES | null}
 */
function universeFromCollectionHandle(handle) {
  if (!handle || handle === SECONDE_MAIN_HANDLE) return null;
  for (const [key, conf] of Object.entries(UNIVERSES)) {
    if (conf.handles.includes(handle) || conf.primary === handle) return /** @type {keyof typeof UNIVERSES} */ (key);
  }
  if (/allait|breastfeed/i.test(handle)) return 'allaitement';
  if (/matern|grossesse|pregnan/i.test(handle)) return 'grossesse';
  if (/kids|enfant|bebe|baby/i.test(handle)) return 'kids';
  if (/access/i.test(handle)) return 'accessoires';
  return null;
}

/**
 * @param {{ type?: string, tags?: string[], collections?: string[] }} signal
 * @returns {keyof typeof UNIVERSES | null}
 */
function universeFromProductSignal(signal) {
  const collections = signal.collections || [];
  for (const handle of collections) {
    const u = universeFromCollectionHandle(handle);
    if (u) return u;
  }
  const tags = (signal.tags || []).map((t) => String(t).toLowerCase());
  const type = (signal.type || '').toLowerCase();
  if (tags.includes('kids') || tags.includes('enfant') || /kids/.test(type)) return 'kids';
  if (tags.includes('nonallaitant') || /maternity|grossesse/.test(type)) return 'grossesse';
  if (tags.includes('allaitant') || /breastfeeding|allait/.test(type)) return 'allaitement';
  if (tags.includes('grossesse') && !tags.includes('allaitant')) return 'grossesse';
  if (tags.some((t) => /access/.test(t)) || /access/.test(type)) return 'accessoires';
  return null;
}

/**
 * @returns {keyof typeof UNIVERSES | null}
 */
function detectUniverse(pageUniverse, signals) {
  for (const c of signals.collections || []) {
    const u = universeFromCollectionHandle(c.handle);
    if (u) return u;
  }
  for (const p of signals.products || []) {
    const u = universeFromProductSignal(p);
    if (u) return u;
  }
  if (pageUniverse && UNIVERSES[pageUniverse]) return /** @type {keyof typeof UNIVERSES} */ (pageUniverse);
  return pageUniverse && universeFromCollectionHandle(pageUniverse)
    ? universeFromCollectionHandle(pageUniverse)
    : null;
}

/**
 * Prefer piece types close to what was viewed (t-shirt, sweat, bas…).
 * @param {{ type?: string, tags?: string[] }} signal
 * @returns {string[]}
 */
function pieceHintsFromSignal(signal) {
  const blob = `${signal.type || ''} ${(signal.tags || []).join(' ')}`.toLowerCase();
  /** @type {string[]} */
  const hints = [];
  if (/sweat|pull/.test(blob)) hints.push('sweat', 'pull');
  if (/tee|t-shirt|tshirt|haut/.test(blob)) hints.push('tee', 't-shirt', 'haut');
  if (/robe|dress/.test(blob)) hints.push('robe', 'dress');
  if (/bas|legging|pantalon|pant/.test(blob)) hints.push('bas', 'legging', 'pantalon');
  if (/lingerie|soutien|bra|culotte/.test(blob)) hints.push('lingerie', 'soutien');
  return hints;
}

/**
 * @param {object} product
 * @param {string[]} hints
 */
function scoreProduct(product, hints, universe) {
  let score = 0;
  const blob = `${product.product_type || ''} ${(product.tags || []).join(' ')} ${product.handle || ''}`.toLowerCase();
  for (const hint of hints) {
    if (blob.includes(hint)) score += 3;
  }
  if (universe === 'grossesse') {
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    if (tags.includes('nonallaitant') || /maternity/.test(blob)) score += 5;
    else if (tags.includes('allaitant') && tags.includes('grossesse')) score += 2;
    else if (tags.includes('allaitant')) score -= 2;
  }
  if (product.available === false) score -= 100;
  return score;
}

/**
 * @param {string} handle
 * @param {number} limit
 */
async function fetchCollectionProducts(handle, limit = 50) {
  try {
    const res = await fetch(`/collections/${handle}/products.json?limit=${limit}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.products) ? data.products : [];
  } catch {
    return [];
  }
}

/**
 * @param {keyof typeof UNIVERSES} universe
 * @param {object} options
 * @param {Set<string>} options.excludeIds
 * @param {string[]} options.pieceHints
 * @param {number} options.limit
 */
async function pickProductIds(universe, { excludeIds, pieceHints, limit }) {
  const conf = UNIVERSES[universe];
  if (!conf) return [];

  /** @type {Map<string, object>} */
  const byId = new Map();

  const primaryProducts = await fetchCollectionProducts(conf.primary, 50);
  for (const p of primaryProducts) byId.set(String(p.id), p);

  // Extra type-specific collections for richer pool (cap requests)
  const extraHandles = conf.handles.filter((h) => h !== conf.primary).slice(0, 2);
  for (const handle of extraHandles) {
    const products = await fetchCollectionProducts(handle, 30);
    for (const p of products) {
      if (!byId.has(String(p.id))) byId.set(String(p.id), p);
    }
  }

  if (universe === 'grossesse' && conf.appointPrimary) {
    const appoint = await fetchCollectionProducts(conf.appointPrimary, 40);
    for (const p of appoint) {
      const tags = (p.tags || []).map((t) => String(t).toLowerCase());
      if (tags.includes(conf.appointRequiresTag || 'grossesse') && !byId.has(String(p.id))) {
        byId.set(String(p.id), p);
      }
    }
  }

  const candidates = [...byId.values()]
    .filter((p) => {
      const id = String(p.id);
      if (excludeIds.has(id)) return false;
      if (isSecondeMainProduct(p)) return false;
      if (p.available === false) return false;
      // variants: available flag on product in products.json is reliable enough
      const anyAvailable = Array.isArray(p.variants)
        ? p.variants.some((/** @type {{ available?: boolean }} */ v) => v.available !== false)
        : true;
      return anyAvailable;
    })
    .map((p) => ({ p, score: scoreProduct(p, pieceHints, universe) }))
    .sort((a, b) => b.score - a.score);

  return candidates.slice(0, limit).map(({ p }) => String(p.id));
}

class PersonalizedRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    if (this.dataset.loaded === 'true') return;

    try {
      const ids = await this.#resolveRecommendationIds();
      if (!ids.length) {
        // Keep Liquid fallback if present; hide only if empty
        if (this.querySelector('[data-has-recommendations="false"]')) {
          this.classList.add('hidden');
        }
        this.dataset.loaded = 'true';
        return;
      }

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const url = new URL(Theme.routes.search_url || '/search', location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('type', 'product');

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(`personalized-recommendations[data-section-id="${sectionId}"]`);

      if (next?.innerHTML?.trim()) {
        this.innerHTML = next.innerHTML;
        this.dataset.loaded = 'true';
        this.dataset.hasPersonalized = 'true';
      } else {
        this.dataset.loaded = 'true';
      }
    } catch (error) {
      console.error('Personalized recommendations error:', error);
      this.dataset.loaded = 'true';
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @returns {Promise<string[]>}
   */
  async #resolveRecommendationIds() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const limit = isMobile
      ? Number(this.dataset.maxProductsMobile) || 4
      : Number(this.dataset.maxProducts) || 6;
    const pageUniverse = this.dataset.pageUniverse || '';
    const exclude = new Set(
      String(this.dataset.excludeIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );

    const signals = BrowsingSignals.get();
    const universe = detectUniverse(pageUniverse, signals) || pageUniverse || 'allaitement';

    /** @type {string[]} */
    let pieceHints = [];
    for (const p of signals.products || []) {
      pieceHints.push(...pieceHintsFromSignal(p));
    }
    if (this.dataset.pagePieceHints) {
      pieceHints.push(...this.dataset.pagePieceHints.split(',').filter(Boolean));
    }
    pieceHints = [...new Set(pieceHints)];

    // Universe key may be a collection handle — normalize
    /** @type {keyof typeof UNIVERSES} */
    let key = /** @type {keyof typeof UNIVERSES} */ (universe);
    if (!UNIVERSES[key]) {
      key = universeFromCollectionHandle(universe) || 'allaitement';
    }

    return pickProductIds(key, { excludeIds: exclude, pieceHints, limit });
  }
}

if (!customElements.get('personalized-recommendations')) {
  customElements.define('personalized-recommendations', PersonalizedRecommendations);
}
